/** @jsxImportSource @emotion/react */
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {createSelector} from "reselect";
import {useTranslation} from "react-i18next";

// Libs
import Button from "../common/components/button";

// Components
import DateInput from "../common/components/date-input";
import RenderContent from "../common/components/render-content";

// Hooks
import {useEventOptions} from "./audit-lib";

// Actions
import {readUsers} from "../users/users-action";
import {readClients} from "../clients/clients-actions";
import {readEvents} from "./audit-actions";

// Selectors
import {usersSelector} from "../users/users-selectors";
import {clientsSelector} from "../clients/clients-selectors";
import {auditSelector} from "./audit-selectors";
import SelectInput from "../common/components/select-input";
import {primaryColor} from "../common/libs/theme-lib";

// Component Selector
const auditPageSelector = createSelector(
    usersSelector,
    clientsSelector,
    auditSelector,
    (users, clients, events) => {
        users = users.toJS();
        users = Array.isArray(users) ? users : [];
        return {
            users: users.map(u => ({value: u._id, label: u.full_name})) || [],
            clients: clients?.toJS()?.map(c => ({value: c._id, label: c.name})) || [],
            events: events?.toJS() || []
        }
    }
);

// Redux dispatch hook
const useReduxDispatch = () => {
    const dispatch = useDispatch();
    return {
        readUsers: () => dispatch(readUsers()),
        readClients: () => dispatch(readClients()),
        readEvents: (filters) => dispatch(readEvents(filters)),
    }
};

const AuditPage = (props) => {

    // Event options
    const eventOptions = useEventOptions();

    // Redux
    const {users, clients, events} = useSelector(state => auditPageSelector(state));
    const {readUsers, readClients, readEvents} = useReduxDispatch();

    // Sate
    const [event, setEvent] = useState(null);
    const [user, setUser] = useState(null);
    const [client, setClient] = useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    //i18n
    const {t} = useTranslation("audit");

    // state of showWelcomeMessage
    const [showWelcomeMessage, setShowWelcomeMessage] = useState(true);

    useEffect(() => {
        readUsers();
        readClients();
    }, []);

    return (
        <>
            <div style={{
                display: "flex",
                alignItems: "flex-end",
                paddingBottom: "15px"
            }}>
                <div style={{flex: 1}}>
                    <div style={{
                        color: primaryColor,
                        fontSize: 28,
                        fontWeight: "bold",
                        marginBottom: 10
                    }}>{t("title")}</div>
                    <div style={{fontWeight: 100, fontSize: 18}}>{t("description")}</div>
                </div>
                <SelectInput
                    options={eventOptions}
                    name={"eventSelector"}
                    label={t("filters.event")}
                    onChange={(newEvent) => setEvent(newEvent.value)}
                    value={eventOptions.find(u => u.value === event)}
                    style={{marginRight: 15}}
                />
                <SelectInput
                    options={users}
                    name={"userSelector"}
                    label={t("filters.user")}
                    onChange={(newUser) => setUser(newUser.value)}
                    value={users.find(u => u.value === user)}
                    style={{marginRight: 15}}
                />
                <SelectInput
                    options={clients}
                    name={"clientSelector"}
                    label={t("filters.client")}
                    onChange={(newClient) => setClient(newClient.value)}
                    value={clients.find(c => c.value === client)}
                    style={{marginRight: 15}}
                />
                <DateInput
                    labelText={t("common:filters.fromDate")}
                    inputWrapperStyle={{marginRight: 15}}
                    value={fromDate}
                    onChange={newDate => setFromDate(newDate?.startOf("day") || newDate)}
                />
                <DateInput
                    labelText={t("common:filters.toDate")}
                    inputWrapperStyle={{marginRight: 15}}
                    value={toDate}
                    onChange={newDate => setToDate(newDate?.endOf("day") || newDate)}
                />
                <Button
                    text={t("common:actions.search")}
                    action={() => {
                        readEvents({event, user, client, fromDate, toDate});
                        setShowWelcomeMessage(false);
                    }}
                    intent="primary"
                />
            </div>
            <RenderContent
                nameCollection={t("nameCollection")}
                data={events}
                showWelcomeMessage={showWelcomeMessage}
                renderHeader={true}
                renderHeaderText="audit:auditTableHeader"
                renderHeaderValues={{count: events.length}}
                renderHeaderComponents={[<strong>number</strong>]}
                config={{
                    sortable: true,
                    initialState: {sortBy: [{id: "date", desc: true}]},
                    conditionalRowStyle: {
                        dataField: 'error',
                        value: true,
                        style: {backgroundColor: '#ffd3d3'}
                    }
                }}
                columns={[{
                    id: "event",
                    Header: t("auditTableColumns.event"),
                    accessor: row => eventOptions.find(u => u.value === row.event)?.label || row.event,
                    type: "string",
                }, {
                    id: "user",
                    Header: t("auditTableColumns.user"),
                    accessor: "user",
                    type: "string",
                }, {
                    id: "client",
                    Header: t("auditTableColumns.client"),
                    accessor: "client",
                    type: "string",
                }, {
                    id: "date",
                    Header: t("auditTableColumns.date"),
                    accessor: "date",
                    type: "datetime",
                }, {
                    id: "details",
                    Header: t("auditTableColumns.details"),
                    accessor: "details",
                    type: "string",
                }]}
            />
        </>
    );
};

export default AuditPage;